import { Component, Input, OnInit } from "@angular/core";

import { Store, State, Required, Viewstate, DataLayerService, Slot } from "@vattenfall/core";
import { DecimalNullableMeasurementViewModel } from "@vattenfall/self-service";

import { CampaignProductCardFinland, SecondaryColor } from "./campaign-product-card-finland.model";
import { CampaignProductCardFinlandService } from "./campaign-product-card-finland.service";
import { VIEWSTATES } from "./campaign-product-card-finland.viewstates";

/**
 * Product card listing details such as price used for marketing purpose targeting Finnish customers.
 *
 * The editor is able to make customizations such as the choosing product image to use, change the sticker color and hide or display monthly cost. It will accept an arbitrary number of rows in its description.
 */
@Component({
  selector: "campaign-product-card-finland",
  templateUrl: "./campaign-product-card-finland.element.html",
  styleUrls: ["./campaign-product-card-finland.element.scss"],
})
export class CampaignProductCardFinlandElement extends State(CampaignProductCardFinland) implements OnInit {
  @Input()
  @Viewstate(VIEWSTATES)
  public viewstate?: string;
  /**
   * The product identifier to get prices from. Required.
   */
  @Input()
  @Required()
  public productId?: string;
  /**
   * The estimated kWh yearly consumption on which the prices and CO2 emissions are calculated. Required.
   */
  @Input()
  @Required()
  public estimatedKwhConsumption = 0;
  /**
   * The URL to redirect to after a product has been selected
   */
  @Input()
  @Required()
  public redirectUrl?: string;
  /**
   * The consumption code for this product
   */
  @Input()
  @Required()
  public consumptionCode?: string;
  /**
   * The campaign code that should be associated with the product.
   */
  @Input()
  public campaignCode?: string;
  /**
   * The marketing code that should be associated with the product.
   */
  @Input()
  public marketingCode?: string;
  /**
   * The background color of sticker. Default is `darkgreen`.
   */
  @Input()
  public stickerColor: SecondaryColor = "darkgreen";
  /**
   * The slot to display payment option label.
   */
  @Slot() readonly campaignProductCardFinlandPaymentTypeLabel?: string;
  /**
   * The slot to display payment option text.
   */
  @Slot() readonly campaignProductCardFinlandPaymentTypeText?: string;
  /**
   * The slot to display spot guard fee label.
   */
  @Slot() readonly campaignProductCardFinlandSpotGuardFeeLabel?: string;
  /**
   * The slot to display spot guard fee information text.
   */
  @Slot() readonly campaignProductCardFinlandSpotGuardFeeInformation?: string;

  /**
   * Estimated kWh consumption expressed as a unit value
   */
  public get estimatedKwhConsumptionUnit(): DecimalNullableMeasurementViewModel {
    return {
      value: this.estimatedKwhConsumption,
      unit: "KWH",
    };
  }
  /**
   * Estimated Co2 emissions per month expressed as a unit value
   */
  public get estimatedCo2EmissionPerMonthUnit(): DecimalNullableMeasurementViewModel {
    const CO2_EMISSION_RATIO = 0.264;
    return {
      value: this.estimatedKwhConsumptionPerMonth * CO2_EMISSION_RATIO,
      unit: "KG/MONTH",
    };
  }
  /**
   * Estimated electricity price per month expressed as a unit value
   */
  public get estimatedElectricityPricePerMonthUnit(): DecimalNullableMeasurementViewModel {
    return {
      value: this.monthlyFee + this.estimatedKwhConsumptionPerMonth * (this.electricityPriceFixed / 100),
      unit: "EURO/MONTH",
    } as DecimalNullableMeasurementViewModel;
  }
  /**
   * Estimated kWh consumption per month
   */
  public get estimatedKwhConsumptionPerMonth(): number {
    return this.estimatedKwhConsumption / 12;
  }
  /**
   * Electricity price as a floating number with two digits
   */
  public get electricityPriceFixed(): number {
    return +(this.electricityPrice?.value?.toFixed(2) ?? 0);
  }
  /**
   * Monthly discounted or base fee, whichever is lower
   */
  public get monthlyFee(): number {
    return this.discountedMonthlyBaseFee?.value ?? this.monthlyBaseFee?.value ?? 0;
  }
  /**
   * Whether the price is discounted or not
   */
  public get isDiscountedProduct(): boolean {
    return this.discountedMonthlyBaseFee != null ? true : false;
  }

  constructor(
    private store: Store<CampaignProductCardFinland>,
    private dataLayerService: DataLayerService,
    private campaignProductCardFinlandService: CampaignProductCardFinlandService
  ) {
    super();
    super.setStore(store);
  }

  ngOnInit() {
    // Do not fetch when reload is triggered by change in viewstate
    if (this.productId && this.viewstate == null) {
      this.store.setAction("loading");
      this.campaignProductCardFinlandService
        .getCampaignProductCardFinland(this.productId, this.campaignCode)
        .toPromise()
        .then(this.store.setState("completed"))
        .catch(this.store.setState("errorred"));
    }
  }

  public onSelected() {
    // Dispatch add to cart
    if (this.productId && this.agreementName && this.productType)
      this.dataLayerService.ecommerce.add({
        id: this.productId,
        name: this.productType,
        variant: this.agreementName,
      });
    // Redirect to next page
    const queryParameters = new URLSearchParams(
      Object.entries({
        productId: this.productId as string,
        campaignCode: this.campaignCode as string,
        marketingCode: this.marketingCode as string,
        consumptionCode: this.consumptionCode as string,
        estimatedKwhConsumption: this.estimatedKwhConsumption?.toString(),
      }).filter(([_key, value]) => !!value) // Filter out undefined values.
    ).toString();
    window.location.href = `${this.redirectUrl}?${queryParameters}`;
  }
}
